import LOCALSTORAGE_KEYS from 'constants/localStorage';
import QUERY_KEYS from 'network/config/queryKeys';
import { addProductToCart, removeProductFromCart, updateCartItemQty } from 'network/mutations/products';
import { viewCart } from 'network/services/goods';
import { getCustomer } from 'network/services/profile';
import { getStoredBranchId } from 'utils/branch';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { CartState, ServerCartState, cartListAtom, serverCartListAtom } from 'store/atoms/cartAtom';
import { userRoleAtom } from 'store/atoms/userAtom';
import { syncLocalAndServerCart } from '../Goods/utils';

// Custom hook for manual cart invalidation
export const useCartInvalidation = () => {
  const queryClient = useQueryClient();
  const userRole = useRecoilValue(userRoleAtom);
  const branchId = getStoredBranchId();

  const invalidateCart = () => {
    queryClient.invalidateQueries([QUERY_KEYS.CART, userRole, branchId]);
  };

  return { invalidateCart };
};

export const NewUseUpdatedCart = () => {
  const userRole = useRecoilValue(userRoleAtom);
  const branchId = getStoredBranchId();

  const isLoggedIn = !!localStorage.getItem('access_token') || !!sessionStorage.getItem('access_token');

  const { data: profileData } = useQuery({
    queryFn: getCustomer,
    queryKey: [QUERY_KEYS.CUSTOMER_PROFILE],
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: isLoggedIn // Only run if logged in
  });

  const setServerCartState = useSetRecoilState(serverCartListAtom);

  const cartQuery = useQuery({
    queryKey: [QUERY_KEYS.CART, userRole, branchId],
    queryFn: () => viewCart(userRole === 'BUSINESS' ? 'BUSINESS' : 'REGULAR'),
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    cacheTime: 5 * 60 * 1000, // Keep cart data in cache for 5 minutes
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: true, // Only refetch on reconnect
    refetchInterval: false, // Don't auto-refetch
    enabled: isLoggedIn, // Only run if logged in
    onSuccess: data => {
      if (profileData && data) {
        const serverCart: ServerCartState = {};
        setServerCartState(serverCart);
      }
    }
  });

  return cartQuery;
};

export const useUpdatedCart = () => {
  const userRole = useRecoilValue(userRoleAtom);
  const branchId = getStoredBranchId();
  const isLoggedIn = !!localStorage.getItem('access_token') || !!sessionStorage.getItem('access_token');

  const { mutateAsync: addMutate } = useMutation({
    mutationFn: addProductToCart
  });
  const { mutateAsync: updateMutate } = useMutation({
    mutationFn: updateCartItemQty
  });
  const { mutateAsync: removeMutate } = useMutation({
    mutationFn: removeProductFromCart
  });
  const { data: profileData } = useQuery({
    queryFn: getCustomer,
    queryKey: [QUERY_KEYS.CUSTOMER_PROFILE],
    enabled: isLoggedIn // Only run if logged in
  });
  const setCartState = useSetRecoilState(cartListAtom);

  const cartQuery = useQuery({
    queryKey: [QUERY_KEYS.CART, userRole, branchId],
    queryFn: () => viewCart(userRole === 'BUSINESS' ? 'BUSINESS' : 'REGULAR'),
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    cacheTime: 5 * 60 * 1000, // Keep cart data in cache for 5 minutes
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: true, // Only refetch on reconnect
    refetchInterval: false, // Don't auto-refetch
    enabled: isLoggedIn, // Only run if logged in
    onSuccess: data => {
      const serverCart: CartState = {};
      const localCartStr = localStorage.getItem(LOCALSTORAGE_KEYS.CART);
      const localCart: CartState | undefined = localCartStr !== null ? JSON.parse(localCartStr) : undefined;
      if (data) {
        data.orders.forEach(order => {
          serverCart[order.vendorId] = order.products.map(product => {
            return {
              name: product?.productName ?? '',
              quantityAvailable: product?.quantityAvailable ?? 0,
              amount: product.price,
              id: product?.productId ?? 0,
              quantity: product.quantity,
              photo: product?.images ?? '',
              shortDSC: product?.description ?? '',
              minimumOrderQuantity: product?.minimumOrderQuantity ?? 0,
              sellerId: product?.seller.id ?? 0
            };
          });
        });
        if (profileData !== undefined && localStorage.getItem(LOCALSTORAGE_KEYS.CART) !== null) {
          syncLocalAndServerCart(
            data,
            addMutate,
            updateMutate,
            removeMutate,
            setCartState,
            userRole === 'BUSINESS' ? 'BUSINESS' : 'REGULAR'
          );
        } else {
          setCartState(serverCart);
        }
      } else {
        setCartState(localCart ?? {});
      }
    }
  });

  return cartQuery;
};
